import { formatResearcherNames } from 'utils/researcherName';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Loader, Link, ResearcherEmail, Button, Icon } from 'components/common';

import { colours } from 'utils/theme';

import { useAuth, usePost } from 'hooks';
import { getPoster } from 'services/api';
import { format, parseDateString } from 'utils/date';
import { DistinctBy } from 'utils/array';
import NotFound from 'components/Redirects/NotFound';

import {
  Container,
  Row,
  Pane,
  Detail,
  LeftPanes,
  RightPanes,
  InlineFlexGroup,
  DetailWithIcon,
  Title,
  VirtualLocationPip,
} from './styled';
import CTA from './CTA';

const iconProps = {
  size: '1x',
  color: colours.grey,
  style: { width: '20px' },
};

const pluraliseRole = (role, arr) => {
  if (role === 'Uncategorised') return role;
  if (arr && arr.filter(x => x !== undefined).length > 1) {
    role += 's';
  }
  return role;
};

const Poster = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [{ res: rawPosters, loading, error }] = usePost({
    url: getPoster,
    body: { id },
    onMount: true,
  });

  const posters = [].concat(rawPosters || []);

  useEffect(() => {
    if (posters && posters.length > 0) {
      const posterTitle = posters[0].title || 'No Title';
      const titleAsArray = posterTitle.split(' ');
      const firstThree = titleAsArray.slice(0, 3);
      const title =
        titleAsArray.length > 3 ? `${firstThree.join(' ')}...` : posterTitle;
      document.title = `${title} | SciLeads`;
    }
  }, [posters]);

  if (loading)
    return (
      <Container>
        <LeftPanes>
          <Pane title='Poster Details'>
            <Loader />
          </Pane>
          <Row>
            <Pane title='Session Details'>
              <Loader />
            </Pane>
            <Pane title='Tradeshow Details'>
              <Loader />
            </Pane>
          </Row>
        </LeftPanes>
        <RightPanes>
          <Pane title='Presenters'>
            <Loader />
          </Pane>
          <Pane title='Contributors'>
            <Loader />
          </Pane>
        </RightPanes>
      </Container>
    );

  if (error || (!loading && (!posters || posters.length === 0))) {
    return <NotFound />;
  }

  if (posters && posters[0].showCta) {
    return <CTA />;
  }

  let sessionStartDate, sessionEndDate, sessionStartTime, sessionEndTime;

  // Helper to check if a date is valid (not null, not '0001-01-01T00:00:00')
  const isValidDate = date => !!date && date !== '0001-01-01T00:00:00';

  // Try to get formatted date and time from sessionStartDate and sessionEndDate
  let startDateValid = isValidDate(posters[0].sessionStartDate);
  let endDateValid = isValidDate(posters[0].sessionEndDate);

  // sessionDate: prefer sessionStartDate, then sessionEndDate, then fallback
  if (startDateValid) {
    sessionStartDate = format(posters[0].sessionStartDate);
  }
  if (endDateValid) {
    sessionEndDate = format(posters[0].sessionEndDate);
  }

  let multiDay = false;
  if (!startDateValid && !endDateValid) {
    // If both dates are invalid, use sessionDay as fallback
    let parsedSessionDay = null;
    if (isValidDate(posters[0].sessionDay)) {
      const parsedDate = parseDateString(posters[0].sessionDay);
      parsedSessionDay = parsedDate ? format(parsedDate) : null;
    }
    sessionStartDate = parsedSessionDay || posters[0].sessionDay || null;
  }

  if (sessionStartDate && sessionEndDate) {
    if (sessionStartDate !== sessionEndDate) {
      multiDay = true;
    } else {
      // since this is on a single day, we don't need to show the end date,
      // as the start date will already be displayed
      sessionEndDate = null;
    }
  }

  if (startDateValid) {
    sessionStartTime = format(posters[0].sessionStartDate, 'HH:mm');
  }

  if (endDateValid) {
    sessionEndTime = format(posters[0].sessionEndDate, 'HH:mm');
  }

  // If either start or end time is missing, try to get from poster.sessionTime
  if ((!sessionStartTime || !sessionEndTime) && posters[0].sessionTime) {
    const times = posters[0].sessionTime.split('-').map(t => t.trim());
    if (!sessionStartTime && times[0]) sessionStartTime = times[0];
    if (!sessionEndTime && times[1]) sessionEndTime = times[1];
  }

  const groupsByRole = posters.reduce((acc, item) => {
    const role =
      (item.roleType && String(item.roleType).trim()) || 'Uncategorised';
    if (!acc[role]) acc[role] = [];
    acc[role].push(item);
    return acc;
  }, {});

  const ROLE_ORDER = [
    'Session Chair',
    'Moderator',
    'Keynote Speaker',
    'Invited Speaker',
    'Plenary Speaker',
    'Platform Speaker',
    'Panel Speaker',
    'Organiser',
    'Instructor',
    'Speaker',
    'Abstract Presenter',
    'Oral Presenter',
    'Poster Presenter',
    'Contributor',
    'Attendee',
    'Uncategorised',
  ];

  const getRank = role => {
    const i = ROLE_ORDER.findIndex(
      r => r.toLowerCase() === String(role).toLowerCase()
    );
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };

  const sortedRoles = Object.keys(groupsByRole).sort((a, b) => {
    const ra = getRank(a);
    const rb = getRank(b);
    return ra === rb ? a.localeCompare(b) : ra - rb;
  });

  return (
    <Container>
      <LeftPanes>
        <Pane title=''>
          <Detail label='Title'>
            <Title>{posters[0].title}</Title>
          </Detail>
          <Detail label='Session Type'>{posters[0].sessionType}</Detail>
          {posters[0].abstractLink && (
            <Detail>
              <Button
                type='primary'
                onClick={() => window.open(posters[0].abstractLink, '_blank')}
              >
                View Full Abstract
              </Button>
            </Detail>
          )}
        </Pane>
        <Row>
          <Pane title='Session Details'>
            <Detail label='Session Title'>{posters[0].sessionTitle}</Detail>
            <InlineFlexGroup>
              {sessionStartDate && (
                <Detail label={`${multiDay ? 'Start ' : ''}Date`}>
                  {sessionStartDate}
                </Detail>
              )}
              {sessionEndDate && (
                <Detail label={`${multiDay ? 'End ' : ''}Date`}>
                  {sessionEndDate}
                </Detail>
              )}
            </InlineFlexGroup>
            <InlineFlexGroup>
              {sessionStartTime && (
                <Detail label='Start Time'>{sessionStartTime}</Detail>
              )}
              {sessionEndTime && (
                <Detail label='End Time'>{sessionEndTime}</Detail>
              )}
            </InlineFlexGroup>
          </Pane>
          <Pane title='Tradeshow Details'>
            <Detail label='Tradeshow Name'>{posters[0].show}</Detail>
            <InlineFlexGroup>
              <Detail label='Start Date'>
                {format(posters[0].tradeshowStartDate)}
              </Detail>
              <Detail label='End Date'>
                {format(posters[0].tradeshowEndDate)}
              </Detail>
            </InlineFlexGroup>
            <InlineFlexGroup>
              {posters[0].tradeshowIsTbc ? (
                <Detail label='Location'>TBC</Detail>
              ) : !posters[0].tradeshowCountry &&
                !posters[0].tradeshowIsVirtual ? (
                <Detail label='Location'>Unknown</Detail>
              ) : (
                <>
                  {posters[0].tradeshowCity && (
                    <Detail label='City'>{posters[0].tradeshowCity}</Detail>
                  )}
                  {posters[0].tradeshowState && (
                    <Detail label='State / Province'>
                      {posters[0].tradeshowState}
                    </Detail>
                  )}
                  {posters[0].tradeshowCountry && (
                    <Detail label='Country'>
                      {posters[0].tradeshowCountry}
                    </Detail>
                  )}
                  {posters[0].tradeshowIsVirtual && (
                    <VirtualLocationPip>
                      <Icon icon='video' size='1x' color={colours.grey} />
                      Virtual
                    </VirtualLocationPip>
                  )}
                </>
              )}
            </InlineFlexGroup>
          </Pane>
        </Row>
      </LeftPanes>
      <RightPanes>
        <Pane>
          {sortedRoles.map(role => {
            const people = DistinctBy(
              groupsByRole[role] || [],
              i => i.sciLeadsResearcherId
            );
            if (!people.length) return null;

            // Sort alphabetically by surname (case-insensitive), then by first name
            const peopleSorted = people.slice().sort((a, b) => {
              const aLast = (a.lastName || '').trim();
              const bLast = (b.lastName || '').trim();
              const byLast = aLast.localeCompare(bLast, undefined, {
                sensitivity: 'base',
              });
              if (byLast !== 0) return byLast;
              const aFirst = (a.firstName || '').trim();
              const bFirst = (b.firstName || '').trim();
              return aFirst.localeCompare(bFirst, undefined, {
                sensitivity: 'base',
              });
            });

            return (
              <div key={role}>
                <Title>{pluraliseRole(role, peopleSorted)}</Title>
                {peopleSorted.map(poster => (
                  <div
                    key={
                      poster.sciLeadsResearcherId ||
                      `${poster.firstName}-${poster.lastName}`
                    }
                  >
                    <Row>
                      <DetailWithIcon>
                        <Icon icon='user' {...iconProps} />
                        <Link to={`/researcher/${poster.sciLeadsResearcherId}`}>
                          {formatResearcherNames(
                            poster.firstName,
                            poster.lastName
                          )}
                        </Link>
                      </DetailWithIcon>
                    </Row>

                    {poster.jobRole && (
                      <Row>
                        <DetailWithIcon>
                          <Icon icon='briefcase' {...iconProps} />
                          {poster.jobRole}
                        </DetailWithIcon>
                      </Row>
                    )}

                    {!user.hideResearcherEmail && poster.email && (
                      <Row>
                        <DetailWithIcon>
                          <Icon icon='envelope' {...iconProps} />
                          <ResearcherEmail
                            email={poster.email}
                            emailStatusCategory={poster.emailStatus}
                            hideIcon
                          />
                        </DetailWithIcon>
                      </Row>
                    )}
                    {poster.organisation && (
                      <Row>
                        <DetailWithIcon>
                          <Icon icon='company' {...iconProps} />
                          {poster.superOrgId > 0 ? (
                            <Link to={`/organisation/${poster.superOrgId}`}>
                              {poster.organisation}
                            </Link>
                          ) : (
                            poster.organisation
                          )}
                        </DetailWithIcon>
                      </Row>
                    )}
                    <br />
                  </div>
                ))}
              </div>
            );
          })}
        </Pane>
      </RightPanes>
    </Container>
  );
};

export default Poster;
